import exifr from 'exifr'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Badge, Button, Col, Input, Modal, Result, Row, Space, Spin, Tabs, Typography, message } from 'antd'
import ChatModal from './ChatModal'
import FilePreviewOverlay from './FilePreviewOverlay'
import styled from 'styled-components'
import { apiFetch } from '../../api'
import { needsUrlRefresh } from './presignedUrl'

const { Text } = Typography

const autoNormalize = data => {
  if (!data || typeof data !== 'object') return data
  if (Array.isArray(data)) return data.map(autoNormalize)

  const keys = Object.keys(data)
  const vals = Object.values(data)

  const isCountObject = keys.length > 0 && vals.every(v => typeof v === 'number')
  if (isCountObject) {
    const entries = Object.entries(data).sort((a, b) => b[1] - a[1])
    return entries[0]?.[0] || null
  }

  const isI18nObject = keys.length > 0 && keys.length <= 5 && keys.every(k => /^[a-z]{2}(-[A-Z]{2})?$/.test(k)) && vals.every(v => typeof v === 'string' || v == null)
  if (isI18nObject) return data.de || data.en || vals.find(v => typeof v === 'string') || null

  const normalized = {}
  Object.entries(data).forEach(([key, val]) => {
    normalized[key] = autoNormalize(val)
  })
  return normalized
}

const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ContainerGrid = styled.div`
  display: grid;
  gap: 16px;
`

const ActionsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 40%;
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`

const CenteredLoader = styled.div`
  text-align: center;
  padding: 24px;
`

const SmallNote = styled(Text)`
  && {
    font-size: 12px;
  }
`

const Img = styled.img`
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`

const ExifGrid = styled.div`
  display: grid;
  gap: 16px;
`

const NoteWrap = styled.div`
  margin-top: 8px;
`

const SectionTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  margin: 20px 0 6px 0;
  color: #333;
  letter-spacing: 0.02em;
`

const CategoryDivider = styled.div`
  height: 1px;
  width: 100%;
  background: linear-gradient(to right, #ddd 60%, transparent 100%);
  margin: 14px 0;
`

const StyledDivider = styled.div`
  height: 1px;
  width: 100%;
  background: linear-gradient(to right, rgb(39, 97, 154) 0%, #27619a 20%, #27619a 80%, rgb(39, 97, 154) 100%);
  margin: ${p => p.$margin || '24px 0 16px 0'};
  position: relative;
`

const DividerLabel = styled.span`
  position: absolute;
  left: 50%;
  top: ${p => p.$top || '-10px'};
  transform: translateX(-50%);
  background: white;
  padding: 0 16px;
  font-weight: 600;
  font-size: ${p => p.$fontSize || '13px'};
  color: #27619a;
  white-space: nowrap;
  text-transform: ${p => p.$textTransform || 'uppercase'};
  letter-spacing: ${p => p.$letterSpacing || '0.5px'};
`

const ImageLabel = styled.div`
  text-align: center;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  background: rgba(0, 0, 0, 0.03);
  padding: 4px 8px;
  border-radius: 0 0 8px 8px;
  margin-top: -8px;
`

const ExifImagePreview = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 8px;
  img {
    width: 56px;
    height: 56px;
    object-fit: cover;
    border-radius: 6px;
    border: 2px solid #27619a;
    display: block;
  }
  span {
    font-weight: 600;
    color: #333;
  }
`

const TabLabelWithImage = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
  img {
    width: 22px;
    height: 22px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #e8e8e8;
    display: block;
  }
`

const ExifTabsBar = styled.div`
  overflow: hidden;
`

const PDF = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  background: #f7fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: #e53e3e;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: #4299e1;
  }
`

const ordering = [
  'software',
  'dateTimeOriginal',
  'requiredBackground',
  'requiredHandPose',
  'takenAt',
  'cameras',
  'cameraResolution',
  'cameraWidth',
  'cameraHeight',
  'imageResolution',
  'imageWidth',
  'imageHeight',
  'cameraAspectRatio',
  'cameraFrameRate',
  'cameraError',
  'userAgent',
  'platform',
  'vendor',
  'language',
  'pixelRatio',
  'timestamp',
  'osName',
  'osVersion',
  'cameraCount',
  'cookieEnabled',
  'onLine',
  'maxTouchPoints',
  'gpsTimestamp',
  'gpsLocation',
  'gpsError',
  'latitude',
  'longitude',
  'accuracy',
  'altitude',
  'heading',
  'speed',
  'FNumber',
  'ExposureTime',
  'ISO',
  'FocalLength',
  'LensModel',
  'LensMake',
  'DeviceManufacturer',
  'DeviceModel',
  'makerNote',
  'JFIFVersion',
  'ResolutionUnit',
  'XResolution',
  'YResolution',
  'ImageSize',
  'Megapixels',
  'BitsPerSample',
  'ColorComponents',
  'EncodingProcess',
  'YCbCrSubSampling',
  'ColorSpaceData',
  'ProfileClass',
  'ProfileConnectionSpace',
  'ProfileDescription',
  'ProfileVersion',
  'ProfileCopyright',
  'ProfileDateTime',
  'ProfileFileSignature',
  'ProfileCMMType',
  'ProfileCreator',
  'ProfileID',
  'PrimaryPlatform',
  'MediaWhitePoint',
  'ConnectionSpaceIlluminant',
  'RenderingIntent',
  'RedMatrixColumn',
  'GreenMatrixColumn',
  'BlueMatrixColumn',
  'RedTRC',
  'GreenTRC',
  'BlueTRC',
  'ExifByteOrder',
  'FileType',
  'FileTypeExtension',
  'MIMEType',
  'ThumbnailWidth',
  'ThumbnailHeight',
]

function formatFieldName(key) {
  const translated = i18n(`verification.exif.${key}`)
  return translated !== `verification.exif.${key}` ? translated : key
}

function getResolutionLabel(width, height) {
  const resolutions = [
    { w: 7680, h: 4320, label: '8K UHD' },
    { w: 3840, h: 2160, label: '4K UHD' },
    { w: 2560, h: 1440, label: '2K QHD' },
    { w: 1920, h: 1080, label: 'Full HD' },
    { w: 1280, h: 720, label: 'HD' },
    { w: 854, h: 480, label: 'SD' },
    { w: 640, h: 480, label: 'VGA' },
  ]
  for (const r of resolutions) {
    if ((width === r.w && height === r.h) || (width === r.h && height === r.w)) return r.label
  }
  return null
}

const sorting = (entries, userCommentFields) => {
  const idx = new Map(ordering.map((k, i) => [k, i]))
  return entries.sort(([a], [b]) => {
    const aIsUser = userCommentFields.has(a)
    const bIsUser = userCommentFields.has(b)
    if (aIsUser && !bIsUser) return -1
    if (!aIsUser && bIsUser) return 1
    const ia = idx.has(a) ? idx.get(a) : Number.MAX_SAFE_INTEGER
    const ib = idx.has(b) ? idx.get(b) : Number.MAX_SAFE_INTEGER
    return ia - ib || a.localeCompare(b, 'de', { sensitivity: 'base' })
  })
}

const STATUS_META = {
  open: { color: 'blue', label: i18n('verification.status.open') },
  inReview: { color: 'orange', label: i18n('verification.status.inReview') },
  needsInfo: { color: 'orange', label: i18n('verification.status.needsInfo') },
  approved: { color: 'green', label: i18n('verification.status.verified') },
  rejected: { color: 'red', label: i18n('verification.status.rejected') },
  closed: { color: 'default', label: i18n('verification.status.closed') },
}
const statusMeta = s => STATUS_META[s] || { color: 'default', label: i18n('common.unknown') }

/**
 * HOR-69: Eine Vorschau wird nicht ueber die (ablaufende) URL adressiert, sondern ueber ein
 * stabiles Ziel, das sich nach dem Neu-Laden des Details wieder aufloesen laesst:
 * - eingereichte Bilder ueber ihren S3-Key (Legacy-Eintraege ohne Key ueber den Index),
 * - Uhrenbilder ueber den Feldnamen.
 */
const previewTargetKey = target => (target.kind === 'watch' ? `watch:${target.field}` : `submitted:${target.s3Key ?? target.index}`)

const resolvePreviewUrl = (detail, target) => {
  if (!detail || !target) return null

  if (target.kind === 'watch') {
    const value = detail.watch?.[target.field]
    return typeof value === 'string' && value ? value : null
  }

  const list = Array.isArray(detail.submittedImages) ? detail.submittedImages : []
  const entry = target.s3Key ? list.find(item => item?.s3Key === target.s3Key) : list[target.index]

  return entry?.previewUrl || null
}

export default function VerificationModal({ id, onClose, make, model }) {
  const [data, setData] = useState(null)
  // HOR-92: 'notFound' | 'error' – ein Deep Link auf eine geloeschte/unbekannte ID darf nicht ewig laden
  const [loadError, setLoadError] = useState(null)
  const [chatOpen, setChatOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [activeImageTab, setActiveImageTab] = useState('0')
  const [exifCache, setExifCache] = useState({})
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewImage, setPreviewImage] = useState(null)
  // HOR-69: Kachel, fuer die gerade frische Links nachgeladen werden (Spinner + Doppelklick-Schutz)
  const [previewLoadingKey, setPreviewLoadingKey] = useState(null)

  const mountedRef = useRef(true)
  // Juengster Ladevorgang; aeltere Antworten (Modal-Wechsel, Unmount) werden verworfen.
  const loadSeqRef = useRef(0)
  // Aktuelles Detail fuer async Handler ohne veraltete Closures.
  const dataRef = useRef(null)
  // Zeitpunkt, zu dem die presigned URLs vom Backend kamen (Schleifenschutz, siehe presignedUrl.js).
  const loadedAtRef = useRef(0)
  // Ziel der offenen Vorschau, damit "Vorschau neu laden" den frischen Link zuordnen kann.
  const previewTargetRef = useRef(null)

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      loadSeqRef.current += 1
    }
  }, [])

  /**
   * Laedt das Verifizierungs-Detail (inkl. frischer presigned URLs). Liefert die normalisierten
   * Daten oder null (Fehler bzw. inzwischen ueberholt). Ein Fehler beim Nachladen laesst bereits
   * angezeigte Daten stehen; nur der Erstaufruf zeigt den Fehlerzustand.
   */
  const loadDetail = useCallback(async () => {
    const seq = ++loadSeqRef.current
    const result = await apiFetch(`/verification-requests/${id}`)

    if (seq !== loadSeqRef.current || !mountedRef.current) return null

    if (!result.ok) {
      if (!dataRef.current) setLoadError(result.status === 404 ? 'notFound' : 'error')
      return null
    }

    const normalized = autoNormalize(result.data)

    dataRef.current = normalized
    loadedAtRef.current = Date.now()
    setLoadError(null)
    setData(normalized)

    return normalized
  }, [id])

  useEffect(() => {
    loadDetail()
  }, [loadDetail])

  /**
   * HOR-69: Vorschau oeffnen. Ist der Link abgelaufen (oder laeuft er gleich ab), wird das Detail
   * zuerst neu geladen und die Vorschau erst mit dem frischen Link angezeigt. Schlaegt das fehl,
   * zeigt das Overlay den Fallback statt des iframes (refreshFailed).
   */
  const openPreview = async target => {
    if (previewLoadingKey) return

    const key = previewTargetKey(target)
    let url = resolvePreviewUrl(dataRef.current, target)
    let refreshFailed = false

    if (url && needsUrlRefresh([url], loadedAtRef.current)) {
      setPreviewLoadingKey(key)

      const fresh = await loadDetail()

      if (!mountedRef.current) return

      setPreviewLoadingKey(null)

      const freshUrl = resolvePreviewUrl(fresh, target)

      if (freshUrl) url = freshUrl
      else refreshFailed = true
    }

    if (!url) refreshFailed = true

    previewTargetRef.current = target
    setPreviewImage({ url, isPdf: !!target.isPdf, refreshFailed })
    setPreviewOpen(true)
  }

  // HOR-69: "Vorschau neu laden" aus dem Overlay – bewusst ohne Schleifenschutz (explizite Nutzeraktion).
  const reloadPreview = async () => {
    const target = previewTargetRef.current

    if (!target) return false

    const fresh = await loadDetail()

    if (!mountedRef.current) return false

    const url = resolvePreviewUrl(fresh, target)

    if (!url) {
      setPreviewImage(prev => (prev ? { ...prev, refreshFailed: true } : prev))
      return false
    }

    setPreviewImage({ url, isPdf: !!target.isPdf, refreshFailed: false })

    return true
  }

  const closePreview = () => {
    setPreviewOpen(false)
    previewTargetRef.current = null
  }

  // HOR-92: Bei einem Deep Link ist die Liste (Quelle der Props make/model) noch nicht geladen –
  // Marke/Modell dann aus dem Detail selbst nehmen, damit der Titel nicht leer bleibt.
  const titleMake = make || data?.watch?.referenceNumber?.make || ''
  const titleModel = model || data?.watch?.referenceNumber?.model || ''

  const submitted = useMemo(() => (Array.isArray(data?.submittedImages) ? data.submittedImages : []), [data?.submittedImages])

  const watchImages = useMemo(() => {
    if (!data?.watch) return []
    return [
      { key: 'profileImage', label: i18n('verification.watchImages.profileImage'), url: data.watch.profileImage },
      { key: 'thumbnailImage', label: i18n('verification.watchImages.thumbnailImage'), url: data.watch.thumbnailImage },
      { key: 'leftImage', label: i18n('verification.watchImages.leftImage'), url: data.watch.leftImage },
      { key: 'rightImage', label: i18n('verification.watchImages.rightImage'), url: data.watch.rightImage },
      { key: 'behindImage', label: i18n('verification.watchImages.behindImage'), url: data.watch.behindImage },
      { key: 'papersImage', label: i18n('verification.watchImages.papersImage'), url: data.watch.papersImage },
      { key: 'boxImage', label: i18n('verification.watchImages.boxImage'), url: data.watch.boxImage },
      { key: 'serialNumberImage', label: i18n('verification.watchImages.serialNumberImage'), url: data.watch.serialNumberImage },
    ].filter(img => !!img.url)
  }, [data?.watch])

  const allImagesForExif = useMemo(() => {
    const a = submitted.map((img, idx) => ({
      ...img,
      label: i18n('verification.modal.imageLabel', { index: idx + 1 }),
      type: 'verify',
      index: idx,
    }))
    const b = watchImages.map((img, idx) => ({
      s3Key: img.key,
      previewUrl: img.url,
      label: img.label,
      type: 'watch',
      index: submitted.length + idx,
      exif: null,
    }))
    return [...a, ...b]
  }, [submitted, watchImages])

  // HOR-69: Der Effekt haengt an den URLs selbst, damit er nach einem Neu-Laden (neue Links) erneut laeuft.
  const exifUrlsKey = allImagesForExif
    .map(i => i.previewUrl)
    .filter(Boolean)
    .join('|')

  useEffect(() => {
    if (activeTab !== 'exif') return
    const urls = allImagesForExif.map(i => i.previewUrl).filter(Boolean)
    let cancelled = false
    const load = async url => {
      if (!url || exifCache[url]) return
      try {
        const res = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store' })
        if (!res.ok) throw new Error(String(res.status))
        const httpMime = res.headers.get('content-type') || ''
        const buf = await res.arrayBuffer()
        let parsed = await exifr.parse(buf, true)
        parsed = await augmentWithImageDims(buf, parsed || {}, httpMime)
        parsed = augmentWithContainerInfo(buf, parsed || {}, { mime: httpMime, url })
        if (!cancelled) setExifCache(p => ({ ...p, [url]: parsed || {} }))
      } catch (e) {
        if (!cancelled) setExifCache(p => ({ ...p, [url]: {} }))
      }
    }
    ;(async () => {
      // HOR-69: Abgelaufene Links wuerden hier mit 403 enden und faelschlich "keine EXIF-Daten"
      // anzeigen. Erst frische Links holen; der Effekt laeuft dann ueber exifUrlsKey erneut.
      // Schlaegt das Neu-Laden fehl, bleibt das bisherige Verhalten (leere Tabelle).
      if (needsUrlRefresh(urls, loadedAtRef.current)) {
        const fresh = await loadDetail()
        if (cancelled || fresh) return
      }
      urls.forEach(load)
    })()
    return () => {
      cancelled = true
    }
  }, [activeTab, exifUrlsKey])

  useEffect(() => {
    if (activeTab !== 'exif') return
    const keys = allImagesForExif.map((_, idx) => String(idx))
    if (keys.length && !keys.includes(activeImageTab)) setActiveImageTab('0')
  }, [activeTab, allImagesForExif.length, activeImageTab])

  return (
    <>
      <Modal
        open
        onCancel={onClose}
        footer={null}
        width={1200}
        centered
        title={
          <TitleBar>
            <span>
              {i18n('verification.modal.title')} {titleMake && `• ${titleMake}`}
              {titleMake && titleModel ? `, ${titleModel}` : ''}
            </span>
            {data && (
              <Badge color={statusMeta(data.status).color} text={i18n(`verification.status.${data.status}`) || i18n('common.unknown')} style={{ marginRight: 30 }} />
            )}
          </TitleBar>
        }
      >
        {loadError ? (
          <Result
            status={loadError === 'notFound' ? '404' : 'error'}
            title={i18n(`verification.detailLoad.${loadError}Title`)}
            subTitle={i18n(`verification.detailLoad.${loadError}Message`, { id })}
            extra={
              <Space>
                {loadError === 'error' && (
                  <Button
                    type="primary"
                    onClick={() => {
                      setLoadError(null)
                      loadDetail()
                    }}
                  >
                    {i18n('common.retry')}
                  </Button>
                )}
                <Button onClick={onClose}>{i18n('common.close')}</Button>
              </Space>
            }
          />
        ) : !data ? (
          <CenteredLoader>
            <Spin />
          </CenteredLoader>
        ) : (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'overview',
                label: i18n('verification.modal.overview'),
                children: (
                  <ContainerGrid>
                    {watchImages.length > 0 && watchImages[0]?.url && (
                      <div style={{ textAlign: 'start' }}>
                        <img
                          src={watchImages[0].url}
                          alt={i18n('common.image')}
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '2px solid #e8e8e8',
                            display: 'block',
                          }}
                        />
                      </div>
                    )}
                    <div>
                      <Text type="secondary">{i18n('verification.modal.createdAtLabel', { date: new Date(data.createdAt).toLocaleString() })}</Text>
                      {Array.isArray(data.requiredHandPoses) && data.requiredHandPoses.length > 0 && (
                        <NoteWrap>
                          <Text>
                            <b>{i18n('verification.modal.requiredPoses')}:</b> {data.requiredHandPoses.join(', ')}
                          </Text>
                        </NoteWrap>
                      )}
                      {Array.isArray(data.requiredBackgrounds) && data.requiredBackgrounds.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <Text>
                              <b>{i18n('verification.modal.requiredBackground')}:</b>{' '}
                              {data.requiredBackgrounds.map(b => (b === 'dark' ? i18n('verification.modal.bgDark') : i18n('verification.modal.bgLight'))).join(', ')}
                            </Text>
                          </div>
                          {submitted.length > 0 && submitted[0]?.previewUrl && (
                            <img
                              src={submitted[0].previewUrl}
                              alt={i18n('common.image')}
                              title={i18n('common.image')}
                              style={{
                                width: 80,
                                height: 80,
                                objectFit: 'cover',
                                borderRadius: 4,
                                border: '1px solid #e8e8e8',
                                marginTop: 8,
                                display: 'block',
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <SectionTitle>{i18n('verification.modal.baseData')}</SectionTitle>
                      <div style={{ display: 'grid', gap: 4 }}>
                        <Text>
                          {i18n('verification.modal.brand')}: {data?.watch?.referenceNumber?.make || '–'}
                        </Text>
                        <Text>
                          {i18n('verification.modal.model')}: {data?.watch?.referenceNumber?.model || '–'}
                        </Text>
                        <Text>
                          {i18n('verification.modal.refNumber')}: {data?.watch?.referenceNumber?.number || data?.watch?.reference || '–'}
                        </Text>
                        <Text>
                          {i18n('verification.modal.serialNumber')}: {data?.watch?.serialNumber || '–'}
                        </Text>
                      </div>
                      <CategoryDivider />
                      <SectionTitle>{i18n('verification.modal.features')}</SectionTitle>
                      <div style={{ display: 'grid', gap: 4 }}>
                        <Text>
                          {i18n('verification.modal.housing')}: {data?.watch?.referenceNumber?.housingMaterial || '–'}
                        </Text>
                        <Text>
                          {i18n('verification.modal.housingDiameter')}: {data?.watch?.referenceNumber?.housingDiameter || '–'} mm
                        </Text>
                        <Text>
                          {i18n('verification.modal.dial')}: {data?.watch?.referenceNumber?.housingDial || '–'}
                        </Text>
                        <Text>
                          {i18n('verification.modal.caliber')}: {data?.watch?.referenceNumber?.clockworkCaliber || '–'}
                        </Text>
                        <Text>
                          {i18n('verification.modal.buildYear')}: {data?.watch?.buildYear || '–'}
                        </Text>
                      </div>
                      <CategoryDivider />
                      <SectionTitle>{i18n('verification.modal.authenticity')}</SectionTitle>
                      <div style={{ display: 'grid', gap: 4 }}>
                        <Text>
                          {i18n('verification.modal.box')}: {data?.watch?.box ? i18n('common.yes') : i18n('common.no')}
                        </Text>
                        <Text>
                          {i18n('verification.modal.papers')}: {data?.watch?.papers ? i18n('common.yes') : i18n('common.no')}
                        </Text>
                        <Text>
                          {i18n('verification.modal.bracelet')}: {data?.watch?.referenceNumber?.braceletMaterial || '–'}
                        </Text>
                        <Text>
                          {i18n('verification.modal.clasp')}: {data?.watch?.referenceNumber?.braceletClasp || '–'}
                        </Text>
                      </div>
                      <CategoryDivider />
                    </div>
                    <div>
                      <Button type="primary" onClick={() => setChatOpen(true)}>
                        {i18n('verification.modal.openChat')}
                      </Button>
                    </div>
                    {data.status !== 'approved' && data.status !== 'rejected' && data.status !== 'closed' && (
                      <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                        <SectionTitle>{i18n('common.actions')}</SectionTitle>
                        <ActionsWrapper>
                          <Button
                            type="primary"
                            style={{ flex: '1 1 auto', background: '#67bc1c', borderColor: '#54a71b' }}
                            onClick={async () => {
                              try {
                                const res = await fetch(`${global.WatchStorage.backendUrl}/api/v1/admin/verification-requests/approve`, {
                                  method: 'POST',
                                  credentials: 'include',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${global.WatchStorage.session.token}`,
                                  },
                                  body: JSON.stringify({ verificationId: id }),
                                })
                                if (res.ok || res.status === 201) {
                                  message.success(i18n('verification.modal.approveSuccess'))
                                  onClose()
                                } else {
                                  message.error(i18n('verification.modal.approveError'))
                                }
                              } catch (e) {
                                message.error(i18n('verification.modal.approveError'))
                              }
                            }}
                          >
                            {i18n('verification.modal.approveBtn')}
                          </Button>
                          <Button
                            danger
                            style={{ flex: '1 1 auto', minWidth: '200px' }}
                            onClick={() => {
                              Modal.confirm({
                                title: i18n('verification.modal.rejectTitle'),
                                content: (
                                  <div>
                                    <Text>{i18n('verification.modal.rejectReasonLabel')}</Text>
                                    <Input.TextArea id="reject-reason" rows={4} placeholder={i18n('verification.modal.rejectPlaceholder')} style={{ marginTop: 12 }} />
                                  </div>
                                ),
                                okText: i18n('common.decline'),
                                okType: 'danger',
                                cancelText: i18n('common.cancel'),
                                onOk: async () => {
                                  const reason = document.getElementById('reject-reason')?.value?.trim()
                                  if (!reason) {
                                    message.warning(i18n('verification.modal.rejectReasonRequired'))
                                    return Promise.reject()
                                  }
                                  try {
                                    const res = await fetch(`${global.WatchStorage.backendUrl}/api/v1/admin/verification-requests/reject`, {
                                      method: 'POST',
                                      credentials: 'include',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        Authorization: `Bearer ${global.WatchStorage.session.token}`,
                                      },
                                      body: JSON.stringify({ verificationId: id, reason }),
                                    })
                                    if (res.ok || res.status === 201) {
                                      message.success(i18n('verification.modal.rejectSuccess'))
                                      onClose()
                                    } else {
                                      message.error(i18n('verification.modal.rejectError'))
                                    }
                                  } catch (e) {
                                    message.error(i18n('verification.modal.rejectError'))
                                  }
                                },
                              })
                            }}
                          >
                            {i18n('verification.modal.rejectBtn')}
                          </Button>
                        </ActionsWrapper>
                      </div>
                    )}
                  </ContainerGrid>
                ),
              },
              {
                key: 'images',
                label: i18n('verification.modal.images'),
                children: (
                  <>
                    <Row gutter={[12, 12]}>
                      {submitted.map((img, idx) => {
                        const fileUrl = img.previewUrl || '/missing.jpg'
                        const isPdf = img.mimeType === 'application/pdf' || img.s3Key?.toLowerCase().endsWith('.pdf')
                        const target = { kind: 'submitted', s3Key: img.s3Key || null, index: idx, isPdf }
                        return (
                          <Col key={img.s3Key ?? img.previewUrl ?? String(idx)} xs={12} sm={8} md={6} lg={6}>
                            <div>
                              <Spin spinning={previewLoadingKey === previewTargetKey(target)}>
                                <div style={{ cursor: 'pointer' }} onClick={() => openPreview(target)}>
                                  {isPdf ? <PDF>📄</PDF> : <Img src={fileUrl} alt={i18n('common.image')} />}
                                </div>
                              </Spin>
                              <ImageLabel>{i18n('verification.modal.imageLabel', { index: idx + 1 })}</ImageLabel>
                              <div style={{ marginTop: 4 }}>
                                <SmallNote type="secondary">
                                  {isPdf ? 'PDF' : ''}
                                  {img.requiredHandPose ? `${i18n('verification.modal.pose')}: ${img.requiredHandPose}` : ''}
                                  {img.requiredHandPose && img.requiredBackground ? ' • ' : ''}
                                  {img.requiredBackground
                                    ? `${i18n('verification.modal.background')}: ${img.requiredBackground === 'dark' ? i18n('verification.modal.bgDark') : i18n('verification.modal.bgLight')}`
                                    : ''}
                                </SmallNote>
                              </div>
                              <div>
                                <SmallNote type="secondary">{img.takenAt ? new Date(img.takenAt).toLocaleString() : ''}</SmallNote>
                              </div>
                            </div>
                          </Col>
                        )
                      })}
                      {submitted.length === 0 && (
                        <Col span={24}>
                          <Text type="secondary">{i18n('verification.modal.noImages')}</Text>
                        </Col>
                      )}
                    </Row>
                    {watchImages.length > 0 && (
                      <>
                        <StyledDivider $margin="32px 0 24px 0">
                          <DividerLabel $top="-12px" $fontSize="14px" $textTransform="none" $letterSpacing="0">
                            {i18n('verification.modal.watchImage')}
                          </DividerLabel>
                        </StyledDivider>
                        <Row gutter={[12, 12]}>
                          {watchImages.map(img => (
                            <Col key={img.key} xs={12} sm={8} md={6} lg={6}>
                              <div>
                                <Spin spinning={previewLoadingKey === `watch:${img.key}`}>
                                  <div style={{ cursor: 'pointer' }} onClick={() => openPreview({ kind: 'watch', field: img.key, isPdf: false })}>
                                    <Img src={img.url} alt={img.label} />
                                  </div>
                                </Spin>
                                <ImageLabel>{img.label}</ImageLabel>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </>
                    )}
                  </>
                ),
              },
              {
                key: 'exif',
                label: i18n('verification.modal.exif'),
                children: (
                  <ExifGrid>
                    {allImagesForExif.length === 0 ? (
                      <Text type="secondary">{i18n('verification.modal.noImages')}</Text>
                    ) : (
                      <ExifTabsBar>
                        <Tabs
                          activeKey={activeImageTab}
                          onChange={setActiveImageTab}
                          items={allImagesForExif.map((img, idx) => ({
                            key: String(idx),
                            label: (
                              <TabLabelWithImage>
                                {img.previewUrl && <img src={img.previewUrl} alt={img.label} />}
                                {img.label}
                              </TabLabelWithImage>
                            ),
                            children: (
                              <>
                                {img.previewUrl && (
                                  <ExifImagePreview>
                                    <img src={img.previewUrl} alt={img.label} />
                                    <div>
                                      <span>{img.label}</span>
                                      <br />
                                      {img.type === 'verify' && (
                                        <SmallNote type="secondary">
                                          {img.requiredHandPose && `${i18n('verification.modal.pose')}: ${img.requiredHandPose}`}
                                          {img.requiredHandPose && img.requiredBackground && ' • '}
                                          {img.requiredBackground &&
                                            `${i18n('verification.modal.background')}: ${img.requiredBackground === 'dark' ? i18n('verification.modal.bgDark') : i18n('verification.modal.bgLight')}`}
                                        </SmallNote>
                                      )}
                                      {img.type === 'watch' && <SmallNote type="secondary">{i18n('verification.modal.watchImage')}</SmallNote>}
                                    </div>
                                  </ExifImagePreview>
                                )}
                                <ExifBlock previewUrl={img.previewUrl} fallbackExif={img.exif} cachedExif={exifCache[img.previewUrl]} />
                              </>
                            ),
                          }))}
                        />
                      </ExifTabsBar>
                    )}
                  </ExifGrid>
                ),
              },
            ]}
          />
        )}
      </Modal>

      {chatOpen && <ChatModal isOpen={chatOpen} onClose={() => setChatOpen(false)} requestId={id} make={titleMake} model={titleModel} />}
      {previewOpen && previewImage && (
        <FilePreviewOverlay
          url={previewImage.url}
          isPdf={previewImage.isPdf}
          refreshFailed={previewImage.refreshFailed}
          onReload={reloadPreview}
          onClose={closePreview}
        />
      )}
    </>
  )
}

const toObject = exif => {
  if (!exif) return {}
  if (typeof exif === 'string') {
    try {
      const parsed = JSON.parse(exif)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }
  return typeof exif === 'object' ? exif : {}
}

const flatObject = (obj, prefix = '', result = {}, userCommentFields = new Set()) => {
  if (!obj || typeof obj !== 'object') return { result, userCommentFields }
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key
    if (key.toLowerCase() === 'usercomment' && typeof value === 'string') {
      try {
        const parsed = JSON.parse(value)
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          for (const [subKey, subValue] of Object.entries(parsed)) {
            if (subKey === 'device' && subValue && typeof subValue === 'object') {
              const deviceFlat = flatObject(subValue, '', {}, new Set())
              Object.entries(deviceFlat.result).forEach(([flatKey, flatValue]) => {
                const cleanKey = flatKey.startsWith('browserInfo.') ? flatKey.replace('browserInfo.', '') : flatKey
                result[cleanKey] = flatValue
                userCommentFields.add(cleanKey)
              })
            } else if (subValue && typeof subValue === 'object' && !Array.isArray(subValue)) {
              const nested = flatObject(subValue, subKey, {}, new Set())
              Object.entries(nested.result).forEach(([fk, fv]) => {
                result[fk] = fv
                userCommentFields.add(fk)
              })
            } else {
              result[subKey] = subValue
              userCommentFields.add(subKey)
            }
          }
          continue
        }
      } catch {}
    }
    if (value == null) result[path] = null
    else if (Array.isArray(value)) result[path] = value
    else if (typeof value === 'object' && Object.prototype.toString.call(value) === '[object Object]') {
      const keys = Object.keys(value)
      const isBufferLike = keys.length > 0 && keys.every(k => !isNaN(parseInt(k, 10)))
      if (isBufferLike) {
        const numArray = keys.sort((a, b) => parseInt(a, 10) - parseInt(b, 10)).map(k => value[k])
        result[path] = numArray
      } else {
        const nested = flatObject(value, path, {}, userCommentFields)
        Object.assign(result, nested.result)
        nested.userCommentFields.forEach(f => userCommentFields.add(f))
      }
    } else result[path] = value
  }
  return { result, userCommentFields }
}

function valToString(value, key) {
  if (value == null) return '–'
  if (key === 'FileSize' && typeof value === 'number') return humanFileSize(value)
  if (key === 'Megapixels' && typeof value === 'number') return value.toFixed(3)
  if (['latitude', 'longitude'].includes(key) && typeof value === 'number') return value.toFixed(7) + '°'
  if (key === 'requiredBackground' && typeof value === 'string')
    return value === 'dark' ? i18n('verification.modal.bgDark') : value === 'light' ? i18n('verification.modal.bgLight') : value
  if (key === 'accuracy' && typeof value === 'number') return `±${Math.round(value)} m`
  if (key === 'altitude' && typeof value === 'number') return `${Math.round(value)} m`
  if (key === 'heading' && typeof value === 'number') return `${Math.round(value)}°`
  if (key === 'speed' && typeof value === 'number') return `${value.toFixed(2)} m/s`
  if (key === 'cameraAspectRatio' && typeof value === 'number') return value.toFixed(2)
  if (key === 'cameraFrameRate' && typeof value === 'number') return `${Math.round(value)} fps`
  if (key === 'pixelRatio' && typeof value === 'number') return `${value}x`
  if (['cameraWidth', 'cameraHeight', 'imageHeight', 'imageWidth'].includes(key) && typeof value === 'number') return `${value}px`
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        const dd = String(date.getDate()).padStart(2, '0')
        const mm = String(date.getMonth() + 1).padStart(2, '0')
        const yyyy = date.getFullYear()
        const hh = String(date.getHours()).padStart(2, '0')
        const min = String(date.getMinutes()).padStart(2, '0')
        return `${dd}.${mm}.${yyyy}, ${hh}:${min}`
      }
    }
    if (key && key.toLowerCase().includes('note')) {
      try {
        const parsed = JSON.parse(value)
        if (parsed && typeof parsed === 'object')
          return Object.entries(parsed)
            .map(([k, v]) => `${k}: ${v}`)
            .join(', ')
      } catch {}
    }
    return value
  }
  if (value instanceof Date) {
    const dd = String(value.getDate()).padStart(2, '0')
    const mm = String(value.getMonth() + 1).padStart(2, '0')
    const yyyy = value.getFullYear()
    const hh = String(value.getHours()).padStart(2, '0')
    const min = String(value.getMinutes()).padStart(2, '0')
    return `${dd}.${mm}.${yyyy}, ${hh}:${min}`
  }
  if (typeof value === 'number') {
    if (key && key.toLowerCase().includes('version') && value >= 100 && value <= 65535) {
      const major = Math.floor(value / 256)
      const minor = value % 256
      if (major <= 99 && minor <= 99) return `${major}.${minor.toString().padStart(2, '0')}`
    }
    return value.toString()
  }
  if (Array.isArray(value)) {
    if (value.length > 0 && value.some(v => v && typeof v === 'object')) {
      return value
        .map(item => {
          if (!item || typeof item !== 'object') return String(item)
          return Object.entries(item)
            .map(([k, v]) => `${k}: ${v}`)
            .join(', ')
        })
        .join(' | ')
    }
    if (value.length > 0 && value.every(v => typeof v === 'number')) {
      return value.map(v => (Number.isInteger(v) ? v.toString() : parseFloat(v.toFixed(5)).toString())).join(' ')
    }
    return value.map(v => (v == null ? '–' : String(v))).join(', ')
  }
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView?.(value)) {
    if (key && key.toLowerCase().includes('note') && value instanceof Uint8Array) {
      try {
        const text = new TextDecoder('utf-8').decode(value)
        const parsed = JSON.parse(text)
        if (parsed && typeof parsed === 'object')
          return Object.entries(parsed)
            .map(([k, v]) => `${k}: ${v}`)
            .join(', ')
        if (text.length > 0 && /^[\x20-\x7E\s]*$/.test(text)) return text
      } catch {}
    }
    if (value instanceof Uint8Array && value.length === 20) {
      try {
        const dv = new DataView(value.buffer, value.byteOffset + 8, 12)
        const xyz = []
        for (let i = 0; i < 3; i++) xyz.push(parseFloat((dv.getInt32(i * 4, false) / 65536.0).toFixed(5)))
        if (xyz.every(v => !isNaN(v) && isFinite(v))) return xyz.join(' ')
      } catch {}
    }
    return `[Binary: ${value.byteLength ?? value.length ?? 0} bytes]`
  }
  if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) return `[Binary: ${value.byteLength} bytes]`
  if (typeof value === 'object') {
    const keys = Object.keys(value)
    if (keys.length > 0 && keys.every(k => !isNaN(parseInt(k, 10)))) {
      const numArray = keys.sort((a, b) => parseInt(a, 10) - parseInt(b, 10)).map(k => value[k])
      if (numArray.every(v => typeof v === 'number')) return numArray.map(v => (Number.isInteger(v) ? v.toString() : parseFloat(v.toFixed(5)).toString())).join(' ')
    }
    return `[Binary: ${value.byteLength ?? value.length ?? Object.keys(value).length ?? 0} bytes]`
  }
  return String(value)
}

function normalizeFlatKeys(flat) {
  if (flat.Software && !flat.software) flat.software = flat.Software
  if (!flat.dateTimeOriginal) flat.dateTimeOriginal = flat.DateTimeOriginal || flat.CreateDate || flat.ModifyDate
  flat.imageWidth ||= flat.ImageWidth || flat.ExifImageWidth || flat.PixelXDimension
  flat.imageHeight ||= flat.ImageHeight || flat.ExifImageHeight || flat.PixelYDimension
  if (typeof flat.latitude !== 'number') flat.latitude = flat.GPSLatitude
  if (typeof flat.longitude !== 'number') flat.longitude = flat.GPSLongitude
  if (!flat.DeviceManufacturer && flat.Make) flat.DeviceManufacturer = flat.Make
  if (!flat.DeviceModel && flat.Model) flat.DeviceModel = flat.Model
  flat.FNumber ||= flat.ApertureValue
  flat.ExposureTime ||= flat.ShutterSpeedValue
  flat.ISO ||= flat.ISOSpeedRatings || flat.PhotographicSensitivity
}

function ExifBlock({ fallbackExif, cachedExif }) {
  const data = cachedExif || fallbackExif || {}
  const [location, setLocation] = useState(null)
  const [loadingLocation, setLoadingLocation] = useState(false)

  useEffect(() => {
    if (!data || Object.keys(data).length === 0) return
    const flat = flatObject(toObject(data), '', {}, new Set()).result
    const lat = flat.latitude ?? flat.GPSLatitude
    const lon = flat.longitude ?? flat.GPSLongitude
    const accuracy = flat.accuracy
    if (typeof lat === 'number' && typeof lon === 'number') {
      setLoadingLocation(true)
      fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&accept-language=${global.WatchStorage.session.language.split('_')[0]}`)
        .then(res => res.json())
        .then(json => {
          const addr = json.address || {}
          const parts = []
          if (accuracy && accuracy < 50 && addr.road) {
            const street = addr.house_number ? `${addr.road} ${addr.house_number}` : addr.road
            parts.push(street)
          }
          const district = addr.suburb || addr.neighbourhood || addr.quarter
          if (district) parts.push(district)
          const city = addr.city || addr.town || addr.village
          if (city) parts.push(city)
          if (addr.country) parts.push(addr.country)
          setLocation(parts.length > 0 ? parts.join(' • ') : i18n('verification.exif.locationError'))
        })
        .catch(() => setLocation(i18n('verification.exif.locationError')))
        .finally(() => setLoadingLocation(false))
    }
  }, [data])

  if (!cachedExif && !fallbackExif) return <Text type="secondary">{i18n('verification.exif.exifLoading')}</Text>
  return <ExifTable exif={data} location={location} loadingLocation={loadingLocation} />
}

function ExifTable({ exif, location, loadingLocation }) {
  const obj = toObject(exif)
  let { result: flat, userCommentFields } = flatObject(obj, '', {}, new Set())
  normalizeFlatKeys(flat)

  const width = Number(flat.cameraWidth)
  const height = Number(flat.cameraHeight)
  const imgh = Number(flat.imageHeight)
  const imgw = Number(flat.imageWidth)
  if (width && height) {
    const resLabel = getResolutionLabel(width, height)
    flat.cameraResolution = `${width}×${height}${resLabel ? ` (${resLabel})` : ''}`
    userCommentFields.add('cameraResolution')
  }
  if (imgw && imgh) {
    const resLabel = getResolutionLabel(imgw, imgh)
    flat.imageResolution = `${imgw}×${imgh}${resLabel ? ` (${resLabel})` : ''}`
    userCommentFields.add('imageResolution')
  }
  if (location && !loadingLocation) {
    flat.gpsLocation = location
    userCommentFields.add('gpsLocation')
  }

  const basicFields = new Set(['software', 'dateTimeOriginal', 'requiredBackground', 'requiredHandPose', 'takenAt'])
  const deviceFields = new Set([
    'DeviceManufacturer',
    'DeviceModel',
    'userAgent',
    'platform',
    'vendor',
    'language',
    'pixelRatio',
    'timestamp',
    'osName',
    'osVersion',
    'cameraCount',
    'cameras',
    'cameraResolution',
    'cameraWidth',
    'cameraHeight',
    'imageResolution',
    'imageWidth',
    'imageHeight',
    'cameraAspectRatio',
    'cameraFrameRate',
    'cameraError',
    'cookieEnabled',
    'onLine',
    'maxTouchPoints',
    'FNumber',
    'ExposureTime',
    'ISO',
    'FocalLength',
    'LensModel',
    'LensMake',
  ])
  const gpsFields = new Set(['gpsTimestamp', 'gpsLocation', 'gpsError', 'latitude', 'longitude', 'accuracy', 'altitude', 'heading', 'speed'])

  const entries = sorting(Object.entries(flat), userCommentFields)
  if (!entries.length) return <Text type="secondary">{i18n('verification.exif.noExifData')}</Text>

  const unique = []
  const seenLC = new Set()
  for (const [k, v] of entries) {
    const lc = k.toLowerCase()
    if (seenLC.has(lc)) continue
    seenLC.add(lc)
    unique.push([k, v])
  }

  const basic = []
  const device = []
  const gps = []
  const rest = []
  unique.forEach(([k, v]) => {
    if (basicFields.has(k)) basic.push([k, v])
    else if (gpsFields.has(k)) gps.push([k, v])
    else if (deviceFields.has(k)) device.push([k, v])
    else rest.push([k, v])
  })

  const renderRows = arr =>
    arr.map(([k, v]) => (
      <tr key={k}>
        <td style={{ padding: '6px 8px', fontWeight: 600, verticalAlign: 'top', width: '35%' }}>{formatFieldName(k)}</td>
        <td style={{ padding: '6px 8px', color: '#595959', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{valToString(v, k)}</td>
      </tr>
    ))

  return (
    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px' }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left', padding: '4px 8px' }}>{i18n('verification.exif.field')}</th>
          <th style={{ textAlign: 'left', padding: '4px 8px' }}>{i18n('verification.exif.value')}</th>
        </tr>
      </thead>
      <tbody>
        {basic.length > 0 && renderRows(basic)}
        {device.length > 0 && (
          <>
            <tr>
              <td colSpan="2" style={{ padding: 0 }}>
                <StyledDivider>
                  <DividerLabel>{i18n('verification.exif.sectionDevice')}</DividerLabel>
                </StyledDivider>
              </td>
            </tr>
            {renderRows(device)}
          </>
        )}
        {gps.length > 0 && (
          <>
            <tr>
              <td colSpan="2" style={{ padding: 0 }}>
                <StyledDivider>
                  <DividerLabel>{i18n('verification.exif.sectionGps')}</DividerLabel>
                </StyledDivider>
              </td>
            </tr>
            {renderRows(gps)}
          </>
        )}
        {rest.length > 0 && (
          <>
            <tr>
              <td colSpan="2" style={{ padding: 0 }}>
                <StyledDivider>
                  <DividerLabel>{i18n('verification.exif.sectionTechnical')}</DividerLabel>
                </StyledDivider>
              </td>
            </tr>
            {renderRows(rest)}
          </>
        )}
      </tbody>
    </table>
  )
}

function augmentWithJpegBasics(buf, exif) {
  try {
    const view = new DataView(buf)
    let off = 0
    if (view.getUint16(off) !== 0xffd8) return exif
    off += 2
    while (off + 3 < view.byteLength) {
      let b = view.getUint8(off++)
      while (b !== 0xff && off < view.byteLength) b = view.getUint8(off++)
      if (off >= view.byteLength) break
      while (view.getUint8(off) === 0xff) off++
      const marker = view.getUint8(off++)
      if (marker === 0xda || marker === 0xd9) break
      if (off + 2 > view.byteLength) break
      const len = view.getUint16(off)
      off += 2
      const segStart = off
      const segEnd = segStart + len - 2
      if (segEnd > view.byteLength) break
      if (marker === 0xc0 || marker === 0xc1 || marker === 0xc2) {
        if (off + 6 > segEnd) break
        const precision = view.getUint8(off)
        off += 1
        const height = view.getUint16(off)
        off += 2
        const width = view.getUint16(off)
        off += 2
        const comps = view.getUint8(off)
        off += 1
        let subsampling = ''
        const compSpecBytes = comps * 3
        if (off + compSpecBytes <= segEnd) {
          const H = [],
            V = []
          for (let i = 0; i < comps; i++) {
            view.getUint8(off)
            off += 1
            const hv = view.getUint8(off)
            off += 1
            view.getUint8(off)
            off += 1
            H.push(hv >> 4)
            V.push(hv & 0x0f)
          }
          if (comps === 3) {
            const yH = H[0],
              yV = V[0]
            if (yH === 2 && yV === 2) subsampling = 'YCbCr4:2:0 (2 2)'
            else if (yH === 2 && yV === 1) subsampling = 'YCbCr4:2:2 (2 1)'
            else if (yH === 1 && yV === 1) subsampling = 'YCbCr4:4:4 (1 1)'
            else subsampling = `YCbCr (${yH} ${yV})`
          }
        }
        if (exif.ImageWidth == null) exif.ImageWidth = width
        if (exif.ImageHeight == null) exif.ImageHeight = height
        if (exif.BitsPerSample == null) exif.BitsPerSample = precision
        if (exif.ColorComponents == null) exif.ColorComponents = comps
        if (exif.EncodingProcess == null) exif.EncodingProcess = marker === 0xc2 ? 'Progressive DCT' : 'Baseline DCT, Huffman coding'
        if (exif.YCbCrSubSampling == null && subsampling) exif.YCbCrSubSampling = subsampling
        break
      }
      off = segEnd
    }
  } catch (_) {}
  return exif
}

function humanFileSize(bytes) {
  if (typeof bytes !== 'number' || !isFinite(bytes)) return '–'
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB']
  let i = 0,
    n = bytes
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i++
  }
  const fixed = i === 0 ? String(n) : n.toFixed(2)
  return `${fixed} ${units[i]} (${bytes} bytes)`
}

function augmentWithContainerInfo(buf, exif, meta = {}) {
  const u8 = new Uint8Array(buf)
  const sniff = () => {
    if (u8[0] === 0xff && u8[1] === 0xd8) return { mime: 'image/jpeg', type: 'JPEG', ext: 'jpg' }
    if (u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4e && u8[3] === 0x47) return { mime: 'image/png', type: 'PNG', ext: 'png' }
    if (u8[0] === 0x47 && u8[1] === 0x49 && u8[2] === 0x46) return { mime: 'image/gif', type: 'GIF', ext: 'gif' }
    if (u8[0] === 0x52 && u8[1] === 0x49 && u8[2] === 0x46 && u8[3] === 0x46 && u8[8] === 0x57 && u8[9] === 0x45 && u8[10] === 0x42 && u8[11] === 0x50)
      return { mime: 'image/webp', type: 'WEBP', ext: 'webp' }
    if ((u8[0] === 0x49 && u8[1] === 0x49 && u8[2] === 0x2a && u8[3] === 0x00) || (u8[0] === 0x4d && u8[1] === 0x4d && u8[2] === 0x00 && u8[3] === 0x2a))
      return { mime: 'image/tiff', type: 'TIFF', ext: 'tif' }
    if (u8[0] === 0x25 && u8[1] === 0x50 && u8[2] === 0x44 && u8[3] === 0x46) return { mime: 'application/pdf', type: 'PDF', ext: 'pdf' }
    return {}
  }
  const s = sniff()
  const httpMime = meta.mime?.split(';')[0]?.trim() || ''
  const mime = httpMime || s.mime || ''
  const type = s.type || (mime ? mime.split('/')[1]?.toUpperCase() : undefined)
  const extFromUrl = (meta.url || '').split('?')[0].split('.').pop()?.toLowerCase()
  const ext = extFromUrl && extFromUrl.length <= 5 ? extFromUrl : s.ext || (mime ? mime.split('/')[1] : undefined)
  if (!exif.MIMEType && mime) exif.MIMEType = mime
  if (!exif.FileType && type) exif.FileType = type
  if (!exif.FileTypeExtension && ext) exif.FileTypeExtension = ext
  if (exif.FileSize == null) exif.FileSize = buf.byteLength
  const iw = exif.ImageWidth || exif.ExifImageWidth || exif.PixelXDimension
  const ih = exif.ImageHeight || exif.ExifImageHeight || exif.PixelYDimension
  if (!exif.ImageSize && iw && ih) exif.ImageSize = `${iw}x${ih}`
  if (!exif.Megapixels && iw && ih) exif.Megapixels = parseFloat(((iw * ih) / 1e6).toFixed(3))
  return exif
}

async function augmentWithImageDims(buf, exif, mimeGuess) {
  try {
    const blob = new Blob([buf], { type: mimeGuess || 'image/jpeg' })
    if ('createImageBitmap' in window) {
      const bmp = await createImageBitmap(blob)
      if (!exif.ImageWidth) exif.ImageWidth = bmp.width
      if (!exif.ImageHeight) exif.ImageHeight = bmp.height
    } else {
      await new Promise(resolve => {
        const url = URL.createObjectURL(blob)
        const im = new Image()
        im.onload = () => {
          if (!exif.ImageWidth) exif.ImageWidth = im.naturalWidth
          if (!exif.ImageHeight) exif.ImageHeight = im.naturalHeight
          URL.revokeObjectURL(url)
          resolve()
        }
        im.onerror = () => {
          URL.revokeObjectURL(url)
          resolve()
        }
        im.src = url
      })
    }
    if (!exif.ImageSize && exif.ImageWidth && exif.ImageHeight) exif.ImageSize = `${exif.ImageWidth}x${exif.ImageHeight}`
    if (!exif.Megapixels && exif.ImageWidth && exif.ImageHeight) exif.Megapixels = parseFloat(((exif.ImageWidth * exif.ImageHeight) / 1e6).toFixed(3))
  } catch {}
  return exif
}
